import React from 'react';

interface ResponsiveImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Fallback URL used when no responsive sizes are provided. */
  src: string;
  /** Responsive variants: width descriptor (e.g. "400") → URL. */
  sizes?: Record<string, string>;
  /** HTML sizes attribute, e.g. "(max-width: 640px) 100vw, 50vw". */
  sizesAttr?: string;
}

/**
 * Renders an <img> with srcset when responsive sizes are available.
 * Falls back to a plain src image for legacy assets without variants.
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  sizes,
  sizesAttr,
  loading = 'lazy',
  decoding = 'async',
  ...rest
}) => {
  const entries = Object.entries(sizes || {}).filter(([, url]) => Boolean(url));
  const srcSet = entries.length > 0
    ? entries.map(([width, url]) => `${url} ${width}w`).join(', ')
    : undefined;
  const fallbackSrc = src || entries[0]?.[1] || '';

  return (
    <img
      src={fallbackSrc}
      srcSet={srcSet}
      sizes={sizesAttr || '100vw'}
      loading={loading}
      decoding={decoding}
      {...rest}
    />
  );
};

export default ResponsiveImage;
